import React, { useMemo } from 'react'
import { Plus, Minus, HelpCircle } from 'lucide-react'
import { validateMatrix, emptyMatrix } from '../utils/priceMatrix'

// Fixed Price Matrix editor.
//
// Rows = bedrooms. Columns = bathrooms. Cells = exact service price.
// An empty cell means UNDEFINED (unsupported combination at booking time)
// — it is NOT a $0 price. A $0 price must be typed explicitly.
//
// The matrix also captures `excludedModifierIds`: a stable-ID list of the
// owner's existing modifier groups whose prices are rolled into the
// matrix. Checking a group here prevents the server from adding it on
// top of the matrix base price at job creation. Matching is by ID, so
// renaming a modifier group never affects pricing correctness.

const defaultBedroomRange = [1, 2, 3, 4, 5]
const defaultBathroomRange = [1, 2, 3]

const FixedPriceMatrixEditor = ({ value, onChange, availableModifiers = [] }) => {
  const matrix = value && typeof value === 'object' && value.bedrooms
    ? value
    : emptyMatrix(defaultBedroomRange, defaultBathroomRange)

  const validation = useMemo(() => validateMatrix(matrix), [matrix])

  const updateMatrix = (patch) => {
    const next = { ...matrix, ...patch }
    onChange(next)
  }

  const parsePriceInput = (raw) => {
    if (raw === '' || raw == null) return undefined
    const n = parseFloat(raw)
    if (!Number.isFinite(n) || n < 0) return undefined
    return n
  }

  const setCellPrice = (bed, bath, raw) => {
    const parsed = parsePriceInput(raw)
    const bedKey = String(bed)
    const bathKey = String(bath)
    const nextPrices = { ...(matrix.prices || {}) }
    const row = { ...(nextPrices[bedKey] || {}) }
    if (parsed === undefined) {
      delete row[bathKey]
    } else {
      row[bathKey] = parsed
    }
    if (Object.keys(row).length === 0) {
      delete nextPrices[bedKey]
    } else {
      nextPrices[bedKey] = row
    }
    updateMatrix({ prices: nextPrices })
  }

  const addBedroomRow = () => {
    const bedrooms = Array.isArray(matrix.bedrooms) ? [...matrix.bedrooms] : []
    const next = bedrooms.length === 0 ? 1 : bedrooms[bedrooms.length - 1] + 1
    bedrooms.push(next)
    updateMatrix({ bedrooms })
  }

  const removeBedroomRow = (bed) => {
    const bedrooms = (matrix.bedrooms || []).filter((n) => n !== bed)
    const prices = { ...(matrix.prices || {}) }
    delete prices[String(bed)]
    updateMatrix({ bedrooms, prices })
  }

  const addBathroomColumn = () => {
    const bathrooms = Array.isArray(matrix.bathrooms) ? [...matrix.bathrooms] : []
    const next = bathrooms.length === 0 ? 1 : bathrooms[bathrooms.length - 1] + 1
    bathrooms.push(next)
    updateMatrix({ bathrooms })
  }

  const removeBathroomColumn = (bath) => {
    const bathrooms = (matrix.bathrooms || []).filter((n) => n !== bath)
    const prices = { ...(matrix.prices || {}) }
    const bathKey = String(bath)
    for (const bedKey of Object.keys(prices)) {
      if (bathKey in prices[bedKey]) {
        const row = { ...prices[bedKey] }
        delete row[bathKey]
        if (Object.keys(row).length === 0) delete prices[bedKey]
        else prices[bedKey] = row
      }
    }
    updateMatrix({ bathrooms, prices })
  }

  const toggleExcludedModifier = (modifierId) => {
    const current = new Set((matrix.excludedModifierIds || []).map((id) => String(id)))
    const idStr = String(modifierId)
    if (current.has(idStr)) current.delete(idStr)
    else current.add(idStr)
    updateMatrix({ excludedModifierIds: Array.from(current) })
  }

  const cellValue = (bed, bath) => {
    const row = matrix.prices && matrix.prices[String(bed)]
    if (!row) return ''
    const v = row[String(bath)]
    return v == null ? '' : String(v)
  }

  const bedrooms = matrix.bedrooms || []
  const bathrooms = matrix.bathrooms || []

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-[var(--sf-text-primary)] mb-2 flex items-center gap-1">
          Fixed Price Matrix
          <HelpCircle className="w-4 h-4 text-[var(--sf-text-muted)]" />
        </h4>
        <p className="text-sm text-[var(--sf-text-secondary)]">
          Enter the exact price for each bedroom × bathroom combination. Leave a cell blank to mark the combination as unavailable. $0 is a valid price and must be typed explicitly.
        </p>
      </div>

      <div className="overflow-x-auto border border-[var(--sf-border-light)] rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-[var(--sf-bg-page)]">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-[var(--sf-text-secondary)]">Bedrooms \ Bathrooms</th>
              {bathrooms.map((bath) => (
                <th key={bath} className="px-3 py-2 text-left font-medium text-[var(--sf-text-secondary)]">
                  <div className="flex items-center gap-2">
                    <span>{bath}</span>
                    <button
                      type="button"
                      onClick={() => removeBathroomColumn(bath)}
                      className="text-xs text-[var(--sf-text-muted)] hover:text-red-600"
                      aria-label={`Remove ${bath} bathroom column`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </th>
              ))}
              <th className="px-3 py-2">
                <button
                  type="button"
                  onClick={addBathroomColumn}
                  className="inline-flex items-center gap-1 text-xs text-[var(--sf-blue-500)] hover:text-[var(--sf-blue-600)]"
                >
                  <Plus className="w-3.5 h-3.5" /> Bathrooms
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {bedrooms.map((bed) => (
              <tr key={bed} className="border-t border-[var(--sf-border-light)]">
                <td className="px-3 py-2 font-medium text-[var(--sf-text-primary)]">
                  <div className="flex items-center gap-2">
                    <span>{bed}</span>
                    <button
                      type="button"
                      onClick={() => removeBedroomRow(bed)}
                      className="text-xs text-[var(--sf-text-muted)] hover:text-red-600"
                      aria-label={`Remove ${bed} bedroom row`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
                {bathrooms.map((bath) => (
                  <td key={bath} className="px-3 py-2">
                    <div className="flex items-center border border-[var(--sf-border-light)] rounded px-2 py-1 w-28">
                      <span className="text-xs text-[var(--sf-text-secondary)] flex-shrink-0">$</span>
                      <input
                        type="number"
                        value={cellValue(bed, bath)}
                        onChange={(e) => setCellPrice(bed, bath, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="—"
                        className="flex-1 border-0 focus:ring-0 p-0 ml-1 min-w-0 outline-none bg-transparent"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </td>
                ))}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={addBedroomRow}
        className="inline-flex items-center gap-1 text-sm text-[var(--sf-blue-500)] hover:text-[var(--sf-blue-600)]"
      >
        <Plus className="w-4 h-4" /> Add bedroom row
      </button>

      {availableModifiers.length > 0 && (
        <div className="border-t border-[var(--sf-border-light)] pt-4">
          <h5 className="text-sm font-semibold text-[var(--sf-text-primary)] mb-2">Modifier groups included in matrix price</h5>
          <p className="text-sm text-[var(--sf-text-secondary)] mb-3">
            Check any existing modifier groups whose prices are already captured by the matrix (typically your Bedrooms and Bathrooms selectors). Their option prices will not be added again at booking.
          </p>
          <div className="space-y-2">
            {availableModifiers.map((m) => {
              const idStr = String(m.id)
              const checked = (matrix.excludedModifierIds || []).map((id) => String(id)).includes(idStr)
              return (
                <label key={idStr} className="flex items-center gap-2 text-sm text-[var(--sf-text-primary)]">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleExcludedModifier(m.id)}
                    className="h-4 w-4 text-[var(--sf-blue-500)] border-[var(--sf-border-light)] rounded"
                  />
                  <span>{m.title || m.name || `Modifier ${m.id}`}</span>
                </label>
              )
            })}
          </div>
        </div>
      )}

      {!validation.ok && (
        <div className="text-sm text-red-600" role="alert">
          Matrix issue: {validation.error}
        </div>
      )}
    </div>
  )
}

export default FixedPriceMatrixEditor
